export const ADMIN_USER_ROLE_KEYS = [
  'super_admin',
  'admin',
  'developer',
  'release_manager',
  'tech_support',
  'customer_support',
  'customer_success',
  'marketing',
  'finance',
  'researcher',
] as const;

export type AdminUserRoleKey = (typeof ADMIN_USER_ROLE_KEYS)[number];

// fallow-ignore-next-line unused-export
export const ALL_PERMISSIONS = [
  'admin_users.view',
  'admin_users.manage',
  'admin_users.assign_role',
  'admin_users.reset_password',
  'admin_users.reset_mfa',
  'flags.view',
  'flags.manage',
  'devtools.access',
  'clinic.view',
  'clinic.export',
  'clinic.adhoc_reports',
  'clinic.sandbox.manage',
  'clinic.teardown_messaging',
  'clinic.support_access',
  'clinic_users.view',
  'clinic_users.manage',
  'clinic_users.impersonate',
  'billing.view',
  'stripe.addons.manage',
  'messaging.templates.manage',
  'notifications.broadcast',
  'research.view',
  'clinical_skills.view',
  'dashboard.research',
] as const;

export type Permission = (typeof ALL_PERMISSIONS)[number];

// fallow-ignore-next-line unused-export
export const ROLE_PERMISSIONS: Record<AdminUserRoleKey, readonly Permission[]> = {
  // super_admin auto-grants every permission. When adding a new entry to
  // ALL_PERMISSIONS, deliberately decide whether super_admin should keep
  // implicit access, and if not, list super_admin's perms explicitly.
  super_admin: ALL_PERMISSIONS,
  // admin: every permission EXCEPT admin_users.* — can do everything in the
  // app but cannot view/create/edit/delete other admins or change their
  // roles/passwords/MFA. Auto-grants any new non-admin_users permissions
  // added to ALL_PERMISSIONS.
  admin: ALL_PERMISSIONS.filter(p => !p.startsWith('admin_users.')),
  developer: [
    'flags.view',
    'flags.manage',
    'devtools.access',
    'clinic.view',
    'clinic.adhoc_reports',
    'clinic.sandbox.manage',
    'clinic_users.view',
    'clinic_users.impersonate',
    'clinic.support_access',
  ],
  release_manager: ['flags.view', 'flags.manage', 'clinic.view', 'clinic.sandbox.manage'],
  tech_support: [
    'clinic.view',
    'clinic.sandbox.manage',
    'clinic.teardown_messaging',
    'clinic_users.view',
    'clinic_users.manage',
    'clinic_users.impersonate',
    'clinic.support_access',
  ],
  customer_support: ['clinic.view', 'clinic_users.view'],
  customer_success: [
    'clinic.view',
    'clinic.export',
    'clinic.adhoc_reports',
    'clinic.sandbox.manage',
    'clinic.support_access',
    'clinic_users.view',
    'clinic_users.manage',
    'billing.view',
  ],
  marketing: [
    'clinic.view',
    'clinic_users.view',
    'messaging.templates.manage',
    'notifications.broadcast',
  ],
  finance: [
    'clinic.view',
    'clinic_users.view',
    'billing.view',
    'clinic.adhoc_reports',
    'stripe.addons.manage',
  ],
  researcher: ['research.view', 'clinical_skills.view', 'dashboard.research'],
};

export const expandPermissions = (key: AdminUserRoleKey): Set<Permission> =>
  new Set(ROLE_PERMISSIONS[key]);

export const isAdminUserRoleKey = (s: unknown): s is AdminUserRoleKey =>
  typeof s === 'string' && (ADMIN_USER_ROLE_KEYS as readonly string[]).includes(s);
